import React, { useState, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import {
    SidebarContentBody,
    SidebarContentFooter,
    SidebarContentScrollable,
} from './SidebarContentBody';
import {
    BODY_AREA,
    FOOTER_AREA,
    HEADER_AREA,
    type IntrinsicAttributes,
} from './constants';
import { useStickyLayout } from './useStickyLayout';

interface ContentContainerProps extends IntrinsicAttributes<HTMLDivElement> {
    /** @property {boolean} [$sticky=true] only scroll the body content, keep the header and footer pinned to the container top/bottom */
    $sticky?: boolean;
}

/** Layout component for sidebar content */
const StyledContentContainer = styled.div.attrs<ContentContainerProps>(
    (props) => ({ 'data-test': props['data-test'] ?? 'content-container' })
)<ContentContainerProps>`
    display: grid;
    grid-template-areas: '${HEADER_AREA}' '${BODY_AREA}' '${FOOTER_AREA}';
    grid-template-rows: auto minmax(0, 1fr) auto;
    overflow: hidden;
    position: relative;
    flex-grow: 1;

    ${(props) =>
        !(props.$sticky ?? true) &&
        css`
            grid-template-rows: auto minmax(auto, 1fr) auto;
            overflow-y: auto;

            & ${SidebarContentBody} {
                display: block;
                overflow-y: visible;
            }

            & ${SidebarContentScrollable} {
                flex: initial;
                min-height: initial;
                overflow-y: visible;
            }

            & ${SidebarContentFooter} {
                flex: initial;
                min-height: initial;
            }
        `}
`;

export const SidebarContentContainer = ({
    children,
    ...props
}: {
    children?: ReactNode;
} & Partial<Omit<ContentContainerProps, '$sticky'>>) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const $sticky = useStickyLayout(container);

    return (
        <StyledContentContainer ref={setContainer} $sticky={$sticky} {...props}>
            {children}
        </StyledContentContainer>
    );
};
